import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Coffee, Printer } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getShopByOwner, type Shop } from '../../services/shop-service';
import { tillKitPrintedKey } from './OnboardingChecklist';
import OwnerShell, { OwnerLoadingShell } from './OwnerShell';
import CoralHeader from '../dashboard/CoralHeader';

/**
 * The printable counter card. Rendered twice: inline as an on-screen
 * preview, and via a body-level portal while printing (the global
 * `@media print` rule hides every direct body child EXCEPT
 * `.print-qr-view`, so the sheet must live directly under <body>).
 */
function TillSheet({ shop, shopLink }: { shop: Shop; shopLink: string }) {
  return (
    <div className="text-center p-10 bg-white text-black max-w-[520px] mx-auto">
      {shop.image_url && (
        <img
          src={shop.image_url}
          alt=""
          className="w-20 h-20 object-cover mx-auto mb-4 border-4 border-black"
        />
      )}
      <h1 className="text-4xl font-bold text-black mb-1">{shop.name}</h1>
      <p className="text-xl text-gray-800 mb-6 font-semibold">
        EARN REWARDS EVERY VISIT
      </p>
      <div className="inline-block p-6 border-4 border-black bg-white">
        <QRCodeSVG value={shopLink} size={300} level="H" bgColor="#ffffff" fgColor="#000000" />
      </div>
      <div className="text-left max-w-[360px] mx-auto mt-6 space-y-2">
        <p className="text-base text-black"><strong>1.</strong> Scan the code — or tap your phone on the ★ below</p>
        {/* Apple sign-in is live and has real users. This card gets printed and
            sits on the counter for months, so naming only Google tells an
            iPhone customer the product isn't for them. */}
        <p className="text-base text-black"><strong>2.</strong> Sign in with Google or Apple — takes 10 seconds</p>
        <p className="text-base text-black"><strong>3.</strong> Pay as normal — your stamp lands automatically</p>
      </div>
      {/* NFC tag home: an NTAG213 sticker written with this same shopLink URL
          (see docs/NFC_TAGS.md) goes behind the ★ — NFC reads fine through
          paper/laminate, and the phone opens the claim page on tap. */}
      <div className="mx-auto mt-5 w-28 h-28 border-4 border-dashed border-black flex flex-col items-center justify-center">
        <span className="text-3xl leading-none">★</span>
        <span className="text-xs font-bold mt-1">TAP PHONE HERE</span>
      </div>
      <div className="mt-6 pt-4 border-t-2 border-black text-sm text-gray-700">
        <p><strong>Keep it handy:</strong> iPhone — Safari Share → Add to Home Screen · Android — tap Install</p>
      </div>
      <p className="text-xs text-gray-500 mt-4">Powered by Bit Brew · app.bit-brew.coffee</p>
    </div>
  );
}

export default function TillKit() {
  const { userAddress } = useAuthStore();
  const navigate = useNavigate();
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    getShopByOwner(userAddress)
      .then((s) => { if (!cancelled) setShop(s); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userAddress]);

  function handlePrint() {
    if (!shop) return;
    localStorage.setItem(tillKitPrintedKey(shop.id), '1');
    setPrinting(true);
    // Let the portal mount before opening the print dialog.
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  }

  const appUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const shopLink = shop ? `${appUrl}/?shop=${shop.owner_address}` : '';

  if (loading) {
    return (
      <OwnerLoadingShell active="more" statusLabel="MORE" title="Loading…">
        <div className="bg-white rounded-[10px] h-64 animate-pulse" />
      </OwnerLoadingShell>
    );
  }

  return (
    <OwnerShell
      active="more"
      header={<CoralHeader statusLabel="MORE" eyebrow="OWNER TOOLS" title="Till Kit" />}
    >
      <div className="space-y-3.5 pb-8">
        {/* sr-only: real landmark heading; CoralHeader's title already shows
            the page name visually. */}
        <h1 className="sr-only">Till Kit</h1>

        <button
          onClick={() => navigate('/shops/owner/dashboard')}
          className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Back to dashboard
        </button>

        <div className="bg-white rounded-[10px] p-[18px]">
          <p className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Till Kit</p>
          <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey">
            Print this card and stand it next to your till. Customers scan it once —
            after that, every Square payment stamps them automatically.
          </p>
          <button
            onClick={handlePrint}
            disabled={!shop}
            className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-5 mt-4 flex items-center gap-2 disabled:opacity-30"
          >
            <Printer size={14} /> Print till card
          </button>
        </div>

        {!shop && (
          <div className="bg-white rounded-[10px] p-8 text-center">
            <Coffee size={40} className="mx-auto mb-4 text-coral" />
            <p className="font-heading text-[10px] text-body-grey mb-4">NO SHOP REGISTERED</p>
            <a
              href="/shops/register"
              className="inline-block rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
            >
              Register my shop
            </a>
          </div>
        )}
        {shop && (
          <div className="rounded-[10px] overflow-hidden border-2 border-hairline">
            <TillSheet shop={shop} shopLink={shopLink} />
          </div>
        )}
      </div>

      {/* Print portal — must be a direct <body> child for the print CSS. */}
      {printing && shop &&
        createPortal(
          <div className="print-qr-view fixed inset-0 z-[200] bg-white flex items-center justify-center overflow-auto">
            <TillSheet shop={shop} shopLink={shopLink} />
          </div>,
          document.body,
        )}
    </OwnerShell>
  );
}
